import { BuildingsIcon, DoorIcon, Icon as PhosphorIcon, UserIcon } from '@phosphor-icons/react'
import { Fragment, MouseEvent, ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ResidentProfileLink,
  ResidentProfileLinkSource,
} from '@/components/business/resident-profile-link'
import { Avatar } from '@/components/ui/avatar'
import { Icon } from '@/components/ui/icon'
import { Tooltip } from '@/components/ui/tooltip'
import { EMPTY_VALUE } from '@/constants'
import { toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { DisplayColumnFields, DisplayFieldType } from '@/models/features/display-column.models'

import { PERSON_DETAILS_SEPARATOR } from '../person-column.constants'
import { Person, PersonDetail, PersonFieldKey, PersonTagField } from '../person-column.types'
import { getPersonCellContent } from '../person-column.utils'
import * as S from './person-cell.styled'

// Note: a click on these copies the value (a staff member's "-" placeholder has nothing to copy)
const COPYABLE_FIELDS: PersonFieldKey[] = ['email', 'phone']

const isCopyable = ({ field, value }: PersonDetail) =>
  COPYABLE_FIELDS.includes(field) && value !== EMPTY_VALUE

const TAG_ICONS: Partial<Record<PersonTagField, PhosphorIcon>> = {
  apartment: DoorIcon,
  buildingNumber: BuildingsIcon,
}

export interface PersonCellProps {
  person: Person
  fields: DisplayColumnFields
  fieldType: DisplayFieldType
  source: ResidentProfileLinkSource
  extra?: ReactNode
  leadingDetail?: string | null
}

export const PersonCell = ({
  person,
  fields,
  fieldType,
  source,
  extra,
  leadingDetail,
}: PersonCellProps) => {
  const { t } = useTranslation(['person-column'])
  const textRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const { name, showAvatar, tags, details } = getPersonCellContent(t, person, fields, fieldType)

  const tagLabels: Record<PersonTagField, string> = {
    apartment: t('person-column:cell.tags.apartment'),
    buildingNumber: t('person-column:cell.tags.buildingNumber'),
    role: t('person-column:cell.tags.role'),
  }

  // Note: same rule as ui/table's TableCell - the full-values tooltip only when something is cut off
  const checkTruncation = () => {
    const overflowing = Array.from(textRef.current?.querySelectorAll('*') ?? []).some(
      (element) => element.scrollWidth > element.clientWidth,
    )
    setIsTruncated(overflowing)
  }

  const firstLine = [name, ...tags.map(({ value }) => value)].join(PERSON_DETAILS_SEPARATOR)
  const secondLine = [leadingDetail, ...details.map(({ value }) => value)]
    .filter(Boolean)
    .join(PERSON_DETAILS_SEPARATOR)

  const copyDetail = async (event: MouseEvent, { field, value }: PersonDetail) => {
    // Note: copying is the whole action - a clickable row or an enclosing link must not react too
    event.preventDefault()
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      // Note: one slot for every copy, and each copy replaces the last one - so copying another
      // value always shows a fresh toast (with that value) instead of piling them up
      toastFactory.success(
        field === 'email'
          ? t('person-column:cell.copy.emailCopied')
          : t('person-column:cell.copy.phoneCopied'),
        { description: value, key: 'person-column-copy', onRepeat: 'replace' },
      )
    } catch {
      toastFactory.error(t('person-column:cell.copy.error'))
    }
  }

  return (
    <S.Cell>
      {showAvatar && (
        <Avatar
          size="small"
          src={getAssetLink({
            fallback: { file: 'avatar.png' },
            file: person.avatar ?? undefined,
            path: 'chitown-avatar/avatar',
          })}
          alt={name}
          fallbackIcon={UserIcon}
        />
      )}
      <S.TextTooltip
        fullWidth
        disabled={!isTruncated}
        trigger={
          <S.Text ref={textRef} onMouseEnter={checkTruncation}>
            <S.FirstLine>
              <S.Name>
                {person.kind === 'resident' ? (
                  <ResidentProfileLink clientId={person.clientId} source={source}>
                    {name}
                  </ResidentProfileLink>
                ) : (
                  name
                )}
              </S.Name>
              {tags.map(({ field, value }) => (
                // Note: once the cell's own full-details tooltip is on (something is cut off), it
                // already lists every tag - a second "Role: …" tooltip would just stack on top.
                <Tooltip
                  key={field}
                  disabled={isTruncated}
                  trigger={
                    <S.Tag size="extra-small" color="neutral" variant="light">
                      {TAG_ICONS[field] && <Icon icon={TAG_ICONS[field]} size="1.1rem" />}
                      {value}
                    </S.Tag>
                  }
                >
                  {`${tagLabels[field]}: ${value}`}
                </Tooltip>
              ))}
            </S.FirstLine>
            {secondLine && (
              <S.Details>
                {leadingDetail}
                {details.map((detail, index) => (
                  <Fragment key={detail.field}>
                    {(leadingDetail || index > 0) && PERSON_DETAILS_SEPARATOR}
                    {isCopyable(detail) ? (
                      <S.CopyValue
                        type="button"
                        aria-label={t('person-column:cell.copy.label', { value: detail.value })}
                        onClick={(event) => copyDetail(event, detail)}
                      >
                        {detail.value}
                      </S.CopyValue>
                    ) : (
                      detail.value
                    )}
                  </Fragment>
                ))}
              </S.Details>
            )}
          </S.Text>
        }
      >
        <S.TooltipLines>
          <span>{firstLine}</span>
          {secondLine && <span>{secondLine}</span>}
        </S.TooltipLines>
      </S.TextTooltip>
      {extra}
    </S.Cell>
  )
}
